import { ComponentType, ReactNode, useEffect, useState } from 'react';
import { ButtonBase, makeStyles } from '@material-ui/core';
import TuneIcon from '@material-ui/icons/Tune';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import ViewModuleOutlinedIcon from '@material-ui/icons/ViewModuleOutlined';
import { FluxSelect, MONO_FONT, fluxTokens } from '../../flux';
import {
  CatalogQueryResult,
  CatalogQueryState,
  CatalogScope,
  FacetOption,
} from './useCatalogQuery';
import { humanizeName, kindLabels, shortRef } from './presentation';

const useStyles = makeStyles(theme => {
  const t = fluxTokens(theme);
  return {
    root: {
      width: 236,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      padding: '14px 14px 16px',
      background: t.panelBg,
      borderRight: `1px solid ${t.border}`,
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        borderRight: 'none',
        borderBottom: `1px solid ${t.border}`,
        maxHeight: 'none',
      },
    },
    head: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingBottom: 10,
      borderBottom: `1px solid ${t.border}`,
    },
    headTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: t.text,
      '& svg': { fontSize: 16, color: t.accent },
    },
    reset: {
      fontSize: 12,
      fontWeight: 500,
      color: t.accent,
      fontFamily: 'inherit',
      padding: '2px 4px',
      borderRadius: 4,
      '&.Mui-disabled': { color: t.textMuted, opacity: 0.6 },
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    label: {
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: t.textMuted,
    },
    scopeItem: {
      width: '100%',
      height: 32,
      padding: '0 10px',
      gap: 9,
      borderRadius: 8,
      border: '1px solid transparent',
      justifyContent: 'flex-start',
      fontFamily: 'inherit',
      fontSize: 12.5,
      fontWeight: 500,
      color: t.textSecondary,
      '& svg': { fontSize: 16, color: t.textMuted },
      '&:hover': { background: t.surfaceHover, color: t.text },
    },
    scopeActive: {
      color: t.text,
      fontWeight: 600,
      background: t.accentBg,
      borderColor: t.accentBorder,
      '& svg': { color: t.accent },
      '&:hover': { background: t.accentBg },
    },
    scopeCount: {
      marginLeft: 'auto',
      minWidth: 22,
      height: 18,
      padding: '0 6px',
      borderRadius: 6,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: MONO_FONT,
      fontSize: 10.5,
      color: t.textMuted,
      background: t.chipBg,
    },
    scopeCountActive: {
      color: t.accent,
      background: t.dark ? 'rgba(139,92,246,0.2)' : 'rgba(110,58,143,0.12)',
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 5,
    },
    tag: {
      height: 22,
      padding: '0 7px',
      borderRadius: 5,
      fontFamily: MONO_FONT,
      fontSize: 10.5,
      color: t.textSecondary,
      background: t.chipBg,
      border: `1px solid ${t.chipBorder}`,
      '&:hover': { borderColor: t.accentBorder, color: t.text },
    },
    tagActive: {
      color: t.accent,
      background: t.accentBg,
      borderColor: t.accentBorder,
    },
    sync: {
      marginTop: 'auto',
      padding: '10px 10px 9px',
      borderRadius: 9,
      border: `1px solid ${t.border}`,
      background: t.surface,
    },
    syncHead: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 11.5,
      fontWeight: 600,
      color: t.text,
    },
    syncStatus: {
      fontFamily: MONO_FONT,
      fontSize: 10.5,
      fontWeight: 600,
    },
    syncBar: {
      height: 4,
      margin: '8px 0 6px',
      borderRadius: 999,
      background: 'linear-gradient(90deg, #8b5cf6 0%, #22c55e 100%)',
    },
    syncBarError: {
      background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 100%)',
    },
    syncMeta: {
      fontFamily: MONO_FONT,
      fontSize: 10,
      color: t.textMuted,
    },
  };
});

const SCOPES: { id: CatalogScope; label: string; icon: ComponentType<any> }[] = [
  { id: 'owned', label: 'Owned', icon: PeopleOutlineIcon },
  { id: 'starred', label: 'Starred', icon: StarBorderIcon },
  { id: 'all', label: 'All Entities', icon: ViewModuleOutlinedIcon },
];

const TAG_PREVIEW = 6;

function useRelativeTime(date?: Date) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick(v => v + 1), 15000);
    return () => window.clearInterval(id);
  }, []);
  if (!date) return '—';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes}m` : `${Math.round(minutes / 60)}h`;
}

const Section = ({ label, children }: { label: string; children: ReactNode }) => {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <span className={classes.label}>{label}</span>
      {children}
    </div>
  );
};

export const CatalogFilters = (props: {
  state: CatalogQueryState;
  update: (patch: Partial<CatalogQueryState>) => void;
  reset: () => void;
  kinds: FacetOption[];
  facets: Record<string, FacetOption[]>;
  counts: { owned?: number; all?: number; starred?: number };
  result: CatalogQueryResult;
  showKind?: boolean;
  allowAnyKind?: boolean;
  defaultScope?: CatalogScope;
}) => {
  const classes = useStyles();
  const { state, update, facets, counts, result } = props;
  const [showAllTags, setShowAllTags] = useState(false);
  const syncedAgo = useRelativeTime(result.fetchedAt);

  const typeFacets = facets['spec.type'] ?? [];
  const lifecycleFacets = facets['spec.lifecycle'] ?? [];
  const ownerFacets = facets['relations.ownedBy'] ?? [];
  const tagFacets = [...(facets['metadata.tags'] ?? [])].sort(
    (a, b) => b.count - a.count,
  );
  const visibleTags = showAllTags ? tagFacets : tagFacets.slice(0, TAG_PREVIEW);

  const hasFilters =
    state.scope !== (props.defaultScope ?? 'owned') ||
    state.types.length > 0 ||
    state.owners.length > 0 ||
    state.lifecycles.length > 0 ||
    state.tags.length > 0 ||
    Boolean(state.text);

  const typePreview = typeFacets
    .slice(0, 3)
    .map(f => f.value)
    .join(', ');

  const kindOptions = props.kinds
    .slice()
    .sort((a, b) => a.value.localeCompare(b.value))
    .map(k => ({
      value: k.value.toLocaleLowerCase('en-US'),
      label: k.value,
      count: k.count,
    }));
  if (props.allowAnyKind) {
    kindOptions.unshift({
      value: '',
      label: 'Todos los kinds',
      count: props.kinds.reduce((sum, k) => sum + k.count, 0),
    });
  } else if (!kindOptions.some(k => k.value === state.kind)) {
    kindOptions.unshift({ value: state.kind, label: kindLabels(state.kind).title, count: 0 });
  }

  return (
    <aside className={classes.root}>
      <div className={classes.head}>
        <span className={classes.headTitle}>
          <TuneIcon /> Filtros &amp; vistas
        </span>
        <ButtonBase className={classes.reset} disabled={!hasFilters} onClick={props.reset}>
          Reset
        </ButtonBase>
      </div>

      <Section label="Scope">
        {SCOPES.map(scope => {
          const active = state.scope === scope.id;
          const Icon = scope.icon;
          const count = counts[scope.id];
          return (
            <ButtonBase
              key={scope.id}
              className={`${classes.scopeItem} ${active ? classes.scopeActive : ''}`}
              onClick={() => update({ scope: scope.id, scopeExplicit: true })}
            >
              <Icon />
              {scope.label}
              <span
                className={`${classes.scopeCount} ${
                  active ? classes.scopeCountActive : ''
                }`}
              >
                {count ?? '…'}
              </span>
            </ButtonBase>
          );
        })}
      </Section>

      {props.showKind !== false && (
      <Section label="Kind">
        <FluxSelect
          value={[state.kind]}
          options={kindOptions}
          placeholder="Kind"
          onChange={([kind]) =>
            update({ kind, types: [], lifecycles: [], owners: [], tags: [] })
          }
        />
      </Section>
      )}

      <Section label="Type">
        <FluxSelect
          multiple
          value={state.types}
          options={typeFacets.map(f => ({ value: f.value.toLocaleLowerCase('en-US'), label: f.value, count: f.count }))}
          placeholder={typePreview ? `All Types (${typePreview})` : 'All Types'}
          allLabel="Todos los tipos"
          onChange={types => update({ types })}
        />
      </Section>

      <Section label="Owner">
        <FluxSelect
          multiple
          value={state.owners}
          options={ownerFacets.map(f => ({
            value: f.value.toLocaleLowerCase('en-US'),
            label: humanizeName(shortRef(f.value)),
            count: f.count,
          }))}
          placeholder="Todos los owners"
          allLabel="Todos los owners"
          onChange={owners => update({ owners })}
        />
      </Section>

      <Section label="Lifecycle">
        <FluxSelect
          multiple
          value={state.lifecycles}
          options={lifecycleFacets.map(f => ({
            value: f.value.toLocaleLowerCase('en-US'),
            label: f.value,
            count: f.count,
          }))}
          placeholder="Cualquier estado"
          allLabel="Cualquier estado"
          onChange={lifecycles => update({ lifecycles })}
        />
      </Section>

      {tagFacets.length > 0 && (
        <Section label="Popular tags">
          <div className={classes.tags}>
            {visibleTags.map(tag => {
              const active = state.tags.includes(tag.value);
              return (
                <ButtonBase
                  key={tag.value}
                  className={`${classes.tag} ${active ? classes.tagActive : ''}`}
                  title={`${tag.count} entidades`}
                  onClick={() =>
                    update({
                      tags: active
                        ? state.tags.filter(t => t !== tag.value)
                        : [...state.tags, tag.value],
                    })
                  }
                >
                  {tag.value}
                </ButtonBase>
              );
            })}
            {tagFacets.length > TAG_PREVIEW && (
              <ButtonBase
                className={classes.tag}
                onClick={() => setShowAllTags(v => !v)}
              >
                {showAllTags ? 'menos' : `+${tagFacets.length - TAG_PREVIEW}`}
              </ButtonBase>
            )}
          </div>
        </Section>
      )}

      <div className={classes.sync}>
        <div className={classes.syncHead}>
          Catalog Sync Engine
          <span
            className={classes.syncStatus}
            style={{ color: result.error ? '#ef4444' : '#22c55e' }}
          >
            {result.error ? 'Error' : 'Healthy'}
          </span>
        </div>
        <div className={`${classes.syncBar} ${result.error ? classes.syncBarError : ''}`} />
        <div className={classes.syncMeta}>
          Synced {syncedAgo} ago
          {result.latencyMs !== undefined ? ` · ${result.latencyMs} ms` : ''}
        </div>
      </div>
    </aside>
  );
};
